import {
    Box,
    Button,
    Grid,
    Skeleton,
    Theme,
    Typography
} from "@mui/material";
import BreadCrum from "../../../lib/BreadCrum";
import { useHistory } from "react-router";
import useQuery from "../../../utils/useQuery";
import { useEffect, useState } from "react";
import { RootStateType } from "../../../redux-store/reducers";
import { useDispatch, useSelector } from "react-redux";
import { navigationData as NavigationData, PaginationObj, SortingObj } from "../types";
import PMSStrategyDetails from "./pmsStrategyDetails";
import {
    ChartIcon,
    CurrencyIcon,
    MoneyGrow,
    PercentageIcons,
    TotalInvestors
} from "../../../assets/SvgIcons";
import PMSOverview from "./pmsOverview";
import { FetchFundHousePerformanceDataArgs, FetchStrategyDetailsArgs, FundHousePerformanceObj, InfoCardProps, PerformersObj, PMSData } from "./types";
import { USER_ROLES } from "../../../utils/constant";
import { GetFundHousePerformanceResponseData, GetSchemeDetailsResponseBody, GetTransactionDetailsResponseBody, GetTop10SchemesRes, SchemeDetailsObj, TotalCardValuesRes, TransactionDetailsData, FetchDataArgs, getNonMfTranDetailsResBody, NonMfTransactionDetailsData } from "../../../redux-store/types/products";
import { getFundHousePerformance, getNonMfTransactionDetails, getSchemeDetails, top10Schemes, totalCardValuesApi } from "../../../redux-store/actions/product";
import { formatCurrency, isModuleAllowed } from "../../../utils/utilityFunctions";
import PMSTransactionDetails from "./pmsTransactionDetails";
import { ZeroStateInvestment } from "../common/ZeroStateInvestment";
import NonMFTransactionDetails from "../common/NonMFTransactionDetails";

const tabsList = ['Overview', 'Strategy Details', 'Transaction Details'];

function InfoCard({
    Icon,
    title,
    value = 0,
    percentage = 0,
    valueTextColor = "text.primary",
    isLoading
}: InfoCardProps) {
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const formatedPercentage = !Number.isNaN(Number(percentage ?? 0)) ?
        Math.round((percentage ?? 0) * 100) / 100 : '0'

    return (
        <Box
            sx={(theme: Theme) => ({
                backgroundColor: theme.palette.common.white,
                borderRadius: "15px",
                boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                border: `3px solid ${theme.palette.stroke.main}`,
                padding: "25px 18px",
                display: "flex",
                height: "100%",
                alignItems: "center",
            })}
        >
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <Icon />
            </Box>
            <Box
                sx={{
                    flex: 3,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    rowGap: "8px",
                    width: '80%',
                }}
            >
                {isLoading && (
                    <>
                        <Skeleton
                            animation="wave"
                            sx={{ width: '60%' }}
                        />
                        <Skeleton
                            animation="wave"
                            sx={{ width: '100%' }}
                        />
                    </>
                )}
                {!isLoading && (
                    <>
                        <Typography
                            variant="subtitle_500"
                            sx={(theme: Theme) => ({
                                lineHeight: "14px",
                                color: theme.palette.text.primary,
                            })}
                        >
                            {title}
                        </Typography>
                        <Typography
                            variant="h3"
                            sx={{
                                lineHeight: "27.5px",
                                color: valueTextColor
                            }}
                        >
                            {title === "Market Value" && (
                                (value ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(value ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: value ?? 0, locale, currencyCode })}`
                            )}
                            {title === "Invested Value" && (
                                (value ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(value ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: value ?? 0, locale, currencyCode })}`
                            )}
                            {title === "Gain/Loss (%)" && (
                                (value ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(value ?? 0), locale, currencyCode })} (${formatedPercentage}%)` : `${formatCurrency({ value: value ?? 0, locale, currencyCode })} (${formatedPercentage}%)`
                            )}
                            {title === "ABSOLUTE RETURN %" && (
                                !Number.isNaN(Number(value ?? 0)) ?
                                    `${Math.round((value ?? 0) * 100) / 100} %` : '0 %'
                            )}
                            {title === "Total Investors" && (
                                value ?? 0
                            )}
                        </Typography>
                    </>
                )}
            </Box>
        </Box>
    )
}

export default function PMS(): JSX.Element {
    const history = useHistory();

    const dispatch = useDispatch()

    const { pathname: pathName, state } = history.location
    const navState = state as NavigationData
    const clientCode = navState?.clientCode ?? ""

    const query = useQuery()

    const initialRouter = pathName.split('/').filter((item: string) => item !== '')

    const { selectedPortfolio } = useSelector((store: RootStateType) => store.selectedPortfiolio);
    const { label: selectedPortfolioLabel = "", value: selectedPortfolioValue = "" } = selectedPortfolio

    const { role = '', permissions = [] } = useSelector((store: RootStateType) => store.auth);

    const { iwappRmId = "", investorClientId = "", clientInterfaceCode } = useSelector((store: RootStateType) => store.id);
    const investorProfile = useSelector((store: RootStateType) => store.investorProfile);
    const { investorRmId = "" } = useSelector((store: RootStateType) => store.sessionData)

    const rmRouterPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
        },
        ...(!initialRouter.includes('product') ? [{
            name: `${initialRouter[1]} ${selectedPortfolioLabel ? `(${selectedPortfolioLabel})` : ''}`,
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: {
                clientCode: navState?.clientCode ?? ""
            }
        }] : []),
        {
            name: 'PMS',
            path: !initialRouter.includes('product') ? `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}` : `/${initialRouter[0]}/${initialRouter[1]}}`,
            state: {
                clientCode: navState?.clientCode ?? ""
            },
            textSx: {
                textTransform: 'uppercase'
            }
        }
    ] : [])

    const investorRouterPath = [
        {
            name: `Dashboard ${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
            path: '/investor-dashboard',
        },
        {
            name: 'PMS',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            state: {
                clientCode: navState?.clientCode ?? ""
            },
            textSx: {
                textTransform: 'uppercase'
            }
        }
    ]

    const initialPmsDataArray = [
        {
            Icon: (props: React.SVGProps<SVGSVGElement>) => <MoneyGrow {...props} viewBox="0 0 57 50" />,
            title: "Market Value",
            value: 0
        },
        {
            Icon: (props: React.SVGProps<SVGSVGElement>) => <CurrencyIcon {...props} />,
            title: "Invested Value",
            value: 0
        },
        {
            Icon: (props: React.SVGProps<SVGSVGElement>) => <ChartIcon {...props} viewBox="0 0 57 50" />,
            title: "Gain/Loss (%)",
            value: 0,
            percentage: 0,
            valueTextColor: 'success.500'
        },
        {
            Icon: initialRouter.includes('product') ? (props: React.SVGProps<SVGSVGElement>) => <TotalInvestors {...props} viewBox="0 0 57 50" /> : (props: React.SVGProps<SVGSVGElement>) => <PercentageIcons {...props} viewBox="0 0 57 50" />,
            title: initialRouter.includes('product') ? "Total Investors" : "ABSOLUTE RETURN %",
            value: 0
        }
    ]

    //Parent component
    const [isLoading, setLoading] = useState<boolean>(false)
    const [selectedTab, setSelectedTab] = useState(query.get('tab') || 'Overview')
    const [pmsDataObj, setPmsDataObj] = useState<{ isSuccess: boolean; data: PMSData[] }>({ isSuccess: true, data: initialPmsDataArray })
    const [isPmsDataArrayLoading, setPmsDataArrayLoading] = useState<boolean>(false)

    //Overview component
    const [fundHousePerformanceTableData, setFundHousePerformanceTableData] = useState<FundHousePerformanceObj>({
        isSuccess: true,
        data: []
    })
    const [isFundHousePerformanceTableDataLoading, setFundHousePerformanceTableDataLoading] = useState<boolean>(false)
    const [fundHousePerformanceTableSortingObj, setFundHousePerformanceTableSortingObj] = useState<SortingObj>({
        order: "asc",
        orderBy: "investedValue"
    })
    const [fundHousePerformanceTablePaginationObj, setFundHousePerformanceTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0
    })
    const [topFivePerformersData, setTopFivePerformersData] = useState<PerformersObj>({
        isSuccess: true,
        data: []
    })
    const [isTopFivePerformersDataLoading, setTopFivePerformersDataLoading] = useState<boolean>(false)
    const [bottomFivePerformersData, setBottomFivePerformersData] = useState<PerformersObj>({
        isSuccess: true,
        data: []
    })
    const [isBottomFivePerformersDataLoading, setBottomFivePerformersDataLoading] = useState<boolean>(false)

    // Strategy Details component
    const [strategyDetailsData, setStrategyDetailsData] = useState<SchemeDetailsObj>({
        isSuccess: true,
        data: []
    })
    const [strategyDetailsTableSortingObj, setStrategyDetailsTableSortingObj] = useState<SortingObj>({
        order: "asc",
        orderBy: "investedValue"
    })
    const [strategyDetailsTablePaginationObj, setStrategyDetailsTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0
    })
    const [strategyDetailsLoading, setStrategyDetailsLoading] = useState<boolean>(false);

    //transaction states and apis 
    const [transactionDetailsLoading, setTransactionDetailsLoading] = useState<boolean>(false);
    const [transactionTableData, setTransactionTableData] = useState<NonMfTransactionDetailsData[]>([]);
    const [allTransactionsData, setAllTransactionsData] = useState<NonMfTransactionDetailsData[]>([]);
    const [transactionDetailPaginationObj, setTransactionDetailPaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0
    })
    const [transactionDetailsData, setTransactionDetailsData] = useState<getNonMfTranDetailsResBody>({} as getNonMfTranDetailsResBody)

    const fetchTransactionDetailsData = async ({
        sortItem = "date",
        order = "desc",
        currentPage = 1,
        rowsPerPage = 5
    }: FetchDataArgs = {}) => {
        try {
            setTransactionDetailsLoading(true)

            const payload = {
                rmId: ![USER_ROLES.INVESTOR].includes(role) ? iwappRmId : '',
                clientCode: [USER_ROLES.INVESTOR].includes(role) ? investorClientId : pathName.includes('investor-profile') ? clientCode || investorProfile?.clientCode || clientInterfaceCode || '' : ``,
                portfolio: [USER_ROLES.INVESTOR].includes(role) ? investorClientId : pathName.includes('investor-profile') ? selectedPortfolio.value : ``,
                category: 'PM'
                // sortItem,
                // order,
                // currentPage,
                // rowsPerPage,
            }

            const transactionDetailsRes = await (dispatch(getNonMfTransactionDetails(payload)) as unknown as Promise<getNonMfTranDetailsResBody>)
            setTransactionDetailsData(transactionDetailsRes)

            if (transactionDetailsRes?.isSuccess) {
                setTransactionTableData(transactionDetailsRes?.result);
                setAllTransactionsData(transactionDetailsRes?.result);
                setTransactionDetailPaginationObj(prevState => ({
                    ...prevState,
                    totalRows: transactionDetailsRes?.totalLength
                }))
            } else {
                console.log(transactionDetailsRes?.message)
            }

        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setTransactionDetailsLoading(false)
        }
    }

    const fetchPMSData = async () => {
        try {
            setPmsDataArrayLoading(true)

            const payload = {
                rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
                category: '',
                clientCode: ![USER_ROLES.INVESTOR].includes(role) ? navState?.clientCode ?? "" : investorClientId,
                portfolioId: !initialRouter.includes('product') ? selectedPortfolio?.value : "",
                assetGroup: "PM"
            }

            const { isSuccess, message, result } = await (dispatch(totalCardValuesApi(payload)) as unknown as Promise<TotalCardValuesRes>)

            if (isSuccess) {
                setPmsDataObj(
                    prevState => ({
                        isSuccess: true,
                        data: prevState.data.map((obj: PMSData) => {
                            if (obj.title === "Market Value") {
                                return {
                                    ...obj,
                                    value: result?.totalMarketValue ?? 0
                                }
                            }
                            if (obj.title === "Invested Value") {
                                return {
                                    ...obj,
                                    value: result?.totalInvestedValue ?? 0
                                }
                            }
                            if (obj.title === "Gain/Loss (%)") {
                                return {
                                    ...obj,
                                    value: result?.gainLoss ?? 0,
                                    percentage: result?.percentage ?? 0,
                                    valueTextColor: (result?.gainLoss ?? 0) >= 0 ? 'success.500' : 'error.500'
                                }
                            }
                            if (obj.title === "ABSOLUTE RETURN %") {
                                return {
                                    ...obj,
                                    value: result?.irr ?? 0,
                                    valueTextColor: (result?.irr ?? 0) >= 0 ? 'success.500' : 'error.500'
                                }
                            }

                            if (obj.title === "Total Investors") {
                                return {
                                    ...obj,
                                    value: result?.totalInvestors ?? 0
                                }
                            }
                            return obj
                        })
                    })
                )
            } else {
                setPmsDataObj({
                    isSuccess: false,
                    data: initialPmsDataArray
                })
            }

            return isSuccess
        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setPmsDataArrayLoading(false)
        }
    }

    const fetchFundHousePerformanceData = async ({
        sortItem = "investedValue",
        order = "asc",
        currentPage = 1,
        rowsPerPage = 5
    }: FetchFundHousePerformanceDataArgs = {}) => {
        try {
            setFundHousePerformanceTableDataLoading(true)

            const payload = {
                rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
                sortItem,
                order,
                assetGroup: "PM",
                currentPage,
                rowsPerPage,
                clientCode: ![USER_ROLES.INVESTOR].includes(role) ? clientCode ?? "" : investorClientId,
                portfolioId: !initialRouter.includes('product') ? selectedPortfolioValue : "",
            }

            const { result = [], totalLength = 0, message = "", isSuccess } = await (dispatch(getFundHousePerformance(payload)) as unknown as Promise<GetFundHousePerformanceResponseData>)

            if (isSuccess) {
                setFundHousePerformanceTableData({
                    isSuccess: true,
                    data: result
                })
                setFundHousePerformanceTablePaginationObj(prevState => ({
                    ...prevState,
                    totalRows: totalLength
                }))
            } else {
                setFundHousePerformanceTableData({
                    isSuccess: false,
                    data: []
                })
                setFundHousePerformanceTablePaginationObj({
                    currentPage: 0,
                    rowsPerPage: 5,
                    totalRows: 0
                })
            }

        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setFundHousePerformanceTableDataLoading(false)
        }

    }

    const fetchTopFivePerformersData = async () => {
        try {
            setTopFivePerformersDataLoading(true)

            const payload = {
                rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
                category: "",
                clientCode: ![USER_ROLES.INVESTOR].includes(role) ? investorProfile?.clientCode || clientInterfaceCode || '' : investorClientId,
                portfolioId: !initialRouter.includes('product') ? selectedPortfolio?.value : "",
                assetGroup: "PM",
                isTop: true
            }

            const { result, message = "", isSuccess } = await (dispatch(top10Schemes(payload)) as unknown as Promise<GetTop10SchemesRes>)

            if (isSuccess) {
                const { topSchemes = { data: [] } } = result
                const { data = [] } = topSchemes

                setTopFivePerformersData({
                    isSuccess: true,
                    data
                })
            } else if (!isSuccess) {
                setTopFivePerformersData({
                    isSuccess: false,
                    data: []
                })
            }

        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setTopFivePerformersDataLoading(false)
        }
    }

    const fetchBottomFivePerformersData = async () => {
        try {
            setBottomFivePerformersDataLoading(true)

            const payload = {
                rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
                category: "",
                clientCode: ![USER_ROLES.INVESTOR].includes(role) ? clientCode ?? "" : investorClientId,
                portfolioId: !initialRouter.includes('product') ? selectedPortfolio?.value : "",
                assetGroup: "PM",
                isTop: false
            }

            const { result, message = "", isSuccess } = await (dispatch(top10Schemes(payload)) as unknown as Promise<GetTop10SchemesRes>)

            if (isSuccess) {
                const { topSchemes = { data: [] } } = result
                const { data = [] } = topSchemes

                setBottomFivePerformersData({
                    isSuccess: true,
                    data
                })

            } else if (!isSuccess) {
                setBottomFivePerformersData({
                    isSuccess: false,
                    data: []
                })
            }

        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setBottomFivePerformersDataLoading(false)
        }
    }

    const fetchStrategyDetails = async ({
        sortItem = "investedValue",
        order = "asc",
        currentPage = 1,
        rowsPerPage = 5
    }: FetchStrategyDetailsArgs = {}) => {
        try {
            setStrategyDetailsLoading(true)

            const payload = {
                rmId: pathName.includes('product') ? `${iwappRmId}` : `${investorRmId}`,
                sortItem,
                order,
                currentPage,
                rowsPerPage,
                clientCode: ![USER_ROLES.INVESTOR].includes(role) ? clientCode ?? "" : investorClientId,
                portfolioId: !initialRouter.includes('product') ? selectedPortfolio?.value : "",
                assetGroup: 'PM'
            }

            const { result, message, isSuccess, totalLength = 0 } = await (dispatch(getSchemeDetails(payload)) as unknown as Promise<GetSchemeDetailsResponseBody>)

            if (isSuccess) {
                setStrategyDetailsData({
                    isSuccess: true,
                    data: result
                })
                setStrategyDetailsTablePaginationObj(prevState => ({
                    ...prevState,
                    totalRows: totalLength
                }))
            } else {
                setStrategyDetailsData({
                    isSuccess: false,
                    data: []
                })
                setStrategyDetailsTablePaginationObj({
                    currentPage: 0,
                    rowsPerPage: 5,
                    totalRows: 0
                })
            }

        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setStrategyDetailsLoading(false)
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            const isSuccess = await fetchPMSData()

            if (isSuccess) {
                if (selectedTab === "Overview") {
                    await Promise.allSettled([
                        fetchFundHousePerformanceData(),
                        fetchTopFivePerformersData(),
                        fetchBottomFivePerformersData()
                    ])
                }
                if (selectedTab === "Strategy Details") {
                    await fetchStrategyDetails()
                }
                if (selectedTab === "Transaction Details") {
                    await fetchTransactionDetailsData()
                }
            }

            setLoading(false)
        }

        fetchData()
    }, [])

    const handleTabChange = async (currentTab: string) => {
        setSelectedTab(currentTab)

        if (currentTab === "Overview") {
            setFundHousePerformanceTableSortingObj({
                order: "asc",
                orderBy: "investedValue"
            })

            setFundHousePerformanceTablePaginationObj({
                currentPage: 0,
                rowsPerPage: 5,
                totalRows: 0
            })

            await Promise.allSettled([
                fetchFundHousePerformanceData(),
                fetchTopFivePerformersData(),
                fetchBottomFivePerformersData()
            ])
        }
        if (currentTab === "Strategy Details") {
            setStrategyDetailsTableSortingObj({
                order: "asc",
                orderBy: "investedValue"
            })

            setStrategyDetailsTablePaginationObj({
                currentPage: 0,
                rowsPerPage: 5,
                totalRows: 0
            })

            await fetchStrategyDetails()
        }
        if (currentTab === "Transaction Details") {
            await Promise.allSettled([
                fetchTransactionDetailsData()
            ])
        }

        history.replace({
            pathname: location.pathname, search: `?tab=${currentTab}`, state: {
                clientCode: navState?.clientCode ?? ""
            }
        })
    }

    const currentInvestedValueObj = pmsDataObj
        .data
        .find(obj => obj.title === "Invested Value")
    const portfolioNotExistStatus = Number(currentInvestedValueObj?.value ?? 0) === 0

    return (
        <Box sx={{ marginTop: "15px" }}>
            <BreadCrum array={![USER_ROLES.INVESTOR].includes(role) ? rmRouterPath : investorRouterPath} />
            <Grid
                container
                sx={{
                    marginTop: "10px",
                    display: "flex",
                }}
                spacing={2}
            >
                {pmsDataObj.data.map((obj: PMSData) => (
                    <Grid
                        item
                        key={obj.title}
                        xs={12}
                        md={6}
                        lg={3}
                    >
                        <InfoCard
                            Icon={obj.Icon}
                            title={obj.title}
                            value={obj.value}
                            percentage={obj?.percentage}
                            valueTextColor={obj?.valueTextColor}
                            isLoading={isPmsDataArrayLoading}
                        />
                    </Grid>
                ))}
            </Grid>

            <Box
                sx={(theme: Theme) => ({
                    display: 'flex',
                    gap: '10px',
                    paddingBottom: '15px',
                    borderBottom: `1px solid ${theme.palette.customColors.pastelGrey}`,
                    overflowX: 'auto',
                    marginTop: "50px"
                })}
            >
                {tabsList.map((item) => {
                    return (
                        <Button
                            variant={selectedTab === item ? "contained" : "text"}
                            key={item}
                            onClick={() => handleTabChange(item)}
                            sx={(theme: Theme) => ({
                                padding: '5px 15px',
                                background: selectedTab === item ? theme.palette.primary[500] : 'transparent',
                                borderRadius: '7px',
                                outline: 'none',
                                border: 'none',
                                cursor: 'pointer'
                            })}
                            disabled={portfolioNotExistStatus || isLoading}
                        >
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    color: selectedTab === item ? theme.palette.textDark.primary : theme.palette.text.secondary
                                })}
                            >
                                {item}
                            </Typography>
                        </Button>
                    )
                })}
            </Box>
            <Box sx={{ marginTop: "25px" }}>
                {(!isLoading && portfolioNotExistStatus) ?
                    <Box
                        sx={(theme: Theme) => ({
                            backgroundColor: theme.palette.common.white,
                            borderRadius: "25px",
                            padding: "25px 30px",
                            height: "50vh"
                        })}
                    >
                        <ZeroStateInvestment />
                    </Box>
                    :
                    (
                        <>
                            {selectedTab === 'Overview' && (
                                <PMSOverview
                                    isLoading={isLoading}
                                    fundHousePerformanceTableSortingObj={fundHousePerformanceTableSortingObj}
                                    setFundHousePerformanceTableSortingObj={setFundHousePerformanceTableSortingObj}
                                    fetchFundHousePerformanceData={fetchFundHousePerformanceData}
                                    fundHousePerformanceTablePaginationObj={fundHousePerformanceTablePaginationObj}
                                    setFundHousePerformanceTablePaginationObj={setFundHousePerformanceTablePaginationObj}
                                    isFundHousePerformanceTableDataLoading={isFundHousePerformanceTableDataLoading}
                                    fundHousePerformanceTableData={fundHousePerformanceTableData}
                                    isTopFivePerformersDataLoading={isTopFivePerformersDataLoading}
                                    topFivePerformersData={topFivePerformersData}
                                    isBottomFivePerformersDataLoading={isBottomFivePerformersDataLoading}
                                    bottomFivePerformersData={bottomFivePerformersData}
                                />
                            )}
                            {selectedTab === 'Strategy Details' && (
                                <PMSStrategyDetails
                                    isLoading={isLoading}
                                    setTableSortingObj={setStrategyDetailsTableSortingObj}
                                    fetchStrategyDetails={fetchStrategyDetails}
                                    tablePaginationObj={strategyDetailsTablePaginationObj}
                                    setTablePaginationObj={setStrategyDetailsTablePaginationObj}
                                    tableSortingObj={strategyDetailsTableSortingObj}
                                    strategyDetailsLoading={strategyDetailsLoading}
                                    strategyDetailsData={strategyDetailsData}
                                />
                            )}
                            {selectedTab === 'Transaction Details' &&
                                // <PMSTransactionDetails clientCode={navState?.clientCode ?? ""} />
                                <NonMFTransactionDetails
                                    transactionDetailsLoading={transactionDetailsLoading}
                                    setTransactionDetailsLoading={setTransactionDetailsLoading}
                                    transactionTableData={transactionTableData}
                                    setTransactionTableData={setTransactionTableData}
                                    transactionDetailPaginationObj={transactionDetailPaginationObj}
                                    setTransactionDetailPaginationObj={setTransactionDetailPaginationObj}
                                    fetchTransactionDetailsData={fetchTransactionDetailsData}
                                    transactionDetailsData={transactionDetailsData}
                                    allTransactionsData={allTransactionsData}
                                />
                            }
                        </>
                    )
                }
            </Box>
        </Box >
    )
}